import { useEffect, useRef, useCallback, useState } from 'react';

const AUTO_SAVE_DEBOUNCE_MS = 1500;

/**
 * Custom hook for debounced auto-save with beforeunload protection.
 *
 * @param {Function} saveFn - Async function to call for saving. Receives no arguments (caller should bind data).
 * @param {boolean} hasChanges - Whether there are unsaved changes.
 * @param {{ onSuccess?: Function, onError?: Function }} [options] - Optional callbacks for save results.
 * @param {number} [changeCounter=0] - Increment on every change to reset the debounce timer even when hasChanges is already true.
 * @returns {{ saveStatus: 'idle'|'saving'|'saved'|'error', triggerSave: () => Promise<boolean> }}
 */
const useAutoSave = (saveFn, hasChanges, { onSuccess, onError } = {}, changeCounter = 0) => {
  const [saveStatus, setSaveStatus] = useState('idle');
  const timerRef = useRef(null);
  const saveFnRef = useRef(saveFn);
  const onSuccessRef = useRef(onSuccess);
  const onErrorRef = useRef(onError);

  // Keep refs current without triggering re-renders
  useEffect(() => { saveFnRef.current = saveFn; }, [saveFn]);
  useEffect(() => { onSuccessRef.current = onSuccess; }, [onSuccess]);
  useEffect(() => { onErrorRef.current = onError; }, [onError]);

  const executeSave = useCallback(async () => {
    setSaveStatus('saving');
    try {
      await saveFnRef.current();
      setSaveStatus('saved');
      if (onSuccessRef.current) onSuccessRef.current();
      return true;
    } catch (err) {
      setSaveStatus('error');
      if (onErrorRef.current) onErrorRef.current(err);
      return false;
    }
  }, []);

  // Saves immediately (fallback button, or before leaving the page). Resolves to true when the save succeeds.
  const triggerSave = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    return executeSave();
  }, [executeSave]);

  // Debounced auto-save when hasChanges becomes true or changeCounter increments
  useEffect(() => {
    if (!hasChanges) return;

    if (timerRef.current) {
      clearTimeout(timerRef.current);
    }

    timerRef.current = setTimeout(() => {
      timerRef.current = null;
      executeSave();
    }, AUTO_SAVE_DEBOUNCE_MS);

    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [hasChanges, changeCounter, executeSave]);

  // Beforeunload guard — warn user if there are unsaved changes
  useEffect(() => {
    if (!hasChanges) return;

    const handleBeforeUnload = (e) => {
      e.preventDefault();
      e.returnValue = '';
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [hasChanges]);

  // Cleanup timer on unmount
  useEffect(() => {
    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
    };
  }, []);

  return { saveStatus, triggerSave };
};

export default useAutoSave;
